import { memo, Suspense, useMemo, useRef, useCallback, useEffect, useState, MutableRefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mergeClasses } from '@fluentui/react-components';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import { useVirtualRows } from './hooks/useVirtualRows';
import { useProjectToggle } from './hooks/useProjectToggle';
import { usePeriodByDate } from './hooks/usePeriodByDate';
import { usePrefetchPreviousPeriod } from './hooks/usePrefetchPreviousPeriod';
import { useGridStore, usePeriodDates } from './state';
import { ProjectRowComponent } from './components/ProjectRow';
import { TaskRowComponent } from './components/TaskRow';
import { TotalRowComponent } from './components/TotalRow';
import { HeaderRowComponent } from './components/HeaderRow';
import { PeriodPicker } from './components/PeriodPicker';
import { TimesheetStatusButton } from './components/TimesheetStatusButton';
import { TaskSearch } from './components/TaskSearch/TaskSearch';
import { PeriodDataDto } from '../../api/generated/v2/generated/data-contracts';
import { useTimeGridStyles } from './useTimeGridStyles';
import { TimeGridSkeleton } from './components/TimeGridSkeleton';
import { SubmitSuccessOverlay } from './components/SubmitSuccessOverlay';

const today = new Date().toISOString();

// Main entry point — PeriodPicker lives outside the suspense-triggering query
export const TimeGrid = () => {
    const [searchParams] = useSearchParams();
    const dateParam = searchParams.get('date');
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const scrollToTaskRef = useRef<((taskId: string) => void) | null>(null);

    const scrollToTask = useCallback((taskId: string) => {
        scrollToTaskRef.current?.(taskId);
    }, []);

    const handleAllocationProjectClick = useCallback((projectName: string) => {
        setSearchQuery(projectName);
        setSearchOpen(true);
    }, []);

    const handleSearchOpenChange = useCallback((open: boolean) => {
        setSearchOpen(open);
        if (!open) {
            setSearchQuery('');
        }
    }, []);

    const styles = useTimeGridStyles();

    return (
        <div className={styles.pageShell}>
            <div className={styles.pageContent}>
                <div className={styles.toolbar}>
                    <div className={styles.toolbarSection}>
                        <TaskSearch externalOpen={searchOpen} externalQuery={searchQuery} onOpenChange={handleSearchOpenChange} onScrollToTask={scrollToTask} />
                    </div>
                    <PeriodPicker />
                    <div className={styles.toolbarSectionEnd}>
                        <TimesheetStatusButton onProjectClick={handleAllocationProjectClick} />
                    </div>
                </div>
                <Suspense fallback={<TimeGridSkeleton />}>
                    <TimeGridWithDate
                        date={dateParam || today.slice(0, today.indexOf('T'))}
                        scrollToTaskRef={scrollToTaskRef}
                        onAllocationProjectClick={handleAllocationProjectClick}
                    />
                </Suspense>
            </div>
        </div>
    );
};

type TimeGridPassthroughProps = {
    scrollToTaskRef: MutableRefObject<((taskId: string) => void) | null>;
    onAllocationProjectClick: (projectName: string) => void;
};

// Used when date search param exists — loads specific period via getUserPeriodByDate
const TimeGridWithDate = ({ date, scrollToTaskRef, onAllocationProjectClick }: { date: string } & TimeGridPassthroughProps) => {
    const { data: periodData } = usePeriodByDate(date);
    usePrefetchPreviousPeriod(periodData);

    return <TimeGridInitializer periodData={periodData} scrollToTaskRef={scrollToTaskRef} onAllocationProjectClick={onAllocationProjectClick} />;
};

// Shared initialization logic — prevents unnecessary re-initializations
const TimeGridInitializer = ({ periodData, scrollToTaskRef, onAllocationProjectClick }: { periodData: PeriodDataDto } & TimeGridPassthroughProps) => {
    const initState = useGridStore(store => store.initState);

    // Only reinitialize when the period itself changes (different id = user navigated to another period).
    // timesheetState changes from submit/retract are handled by setPeriodData in the mutation hooks,
    // so those React Query cache updates must NOT trigger a full store re-init (which would discard local entries).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => initState(periodData), [initState, periodData.id]);

    return <TimeGridInner scrollToTaskRef={scrollToTaskRef} onAllocationProjectClick={onAllocationProjectClick} />;
};

type TimeGridInnerProps = TimeGridPassthroughProps & {
    className?: string;
    style?: React.CSSProperties;
};

const OVERSCAN = 5;

const measureElement = (element: Element, _entry: ResizeObserverEntry, _instance: Virtualizer<HTMLDivElement, Element>) =>
    element?.getBoundingClientRect().height;

const TimeGridInner = memo<TimeGridInnerProps>(function TimeGridInner({ className, style, scrollToTaskRef, onAllocationProjectClick }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const rows = useVirtualRows(); // Get minimal row identifiers
    const dates = usePeriodDates();
    const { handleToggle } = useProjectToggle();
    const styles = useTimeGridStyles();
    const setHighlightedTask = useGridStore(s => s.setHighlightedTask);
    const pendingScrollTaskRef = useRef<string | null>(null);
    const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Virtualizer setup with dynamic heights
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: useCallback((index: number) => rows[index]?.height ?? 60, [rows]),
        overscan: OVERSCAN,
        // Enable dynamic measurement for smooth expand/collapse
        measureElement,
    });

    // Clean up highlight timeout on unmount
    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
            }
        };
    }, []);

    // Expose scrollToTask to parent via ref
    useEffect(() => {
        scrollToTaskRef.current = (taskId: string) => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
            }
            setHighlightedTask(taskId);
            highlightTimeoutRef.current = setTimeout(() => {
                setHighlightedTask(null);
                highlightTimeoutRef.current = null;
            }, 1500);

            const index = rows.findIndex(r => r.type === 'task' && r.taskId === taskId);
            if (index >= 0) {
                rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
            } else {
                // Task not in rows yet (project may be expanding), defer scroll
                pendingScrollTaskRef.current = taskId;
            }
        };
        return () => {
            scrollToTaskRef.current = null;
        };
    }, [rows, rowVirtualizer, scrollToTaskRef, setHighlightedTask]);

    // Handle deferred scroll after rows update (e.g., project expansion)
    useEffect(() => {
        const taskId = pendingScrollTaskRef.current;
        if (!taskId) return;
        const index = rows.findIndex(r => r.type === 'task' && r.taskId === taskId);
        if (index >= 0) {
            pendingScrollTaskRef.current = null;
            rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
        }
    }, [rows, rowVirtualizer]);

    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalHeight = rowVirtualizer.getTotalSize();

    // Render virtual rows (total row is excluded — rendered separately as sticky bottom)
    const renderedRows = useMemo(() => {
        return virtualRows.map(virtualRow => {
            const row = rows[virtualRow.index];
            if (!row) return null;

            const rowStyle: React.CSSProperties = {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
            };

            switch (row.type) {
                case 'project': {
                    return (
                        <ProjectRowComponent
                            key={`project-${row.projectId}`}
                            projectId={row.projectId}
                            dates={dates}
                            onToggle={handleToggle}
                            style={rowStyle}
                        />
                    );
                }
                case 'task': {
                    return (
                        <TaskRowComponent
                            key={`task-${row.taskId}-${row.workType}`}
                            taskId={row.taskId}
                            workType={row.workType}
                            dates={dates}
                            style={rowStyle}
                        />
                    );
                }
                case 'spacer': {
                    return <div key={`spacer-${virtualRow.index}`} style={{ ...rowStyle, height: row.height }} />;
                }
                default: {
                    return null;
                }
            }
        });
    }, [virtualRows, rows, dates, handleToggle]);

    return (
        <div className={mergeClasses(styles.root, className)} style={style}>
            <div ref={scrollRef} className={styles.gridScrollContainer} role="grid" aria-label="Time entries grid">
                <div className={styles.gridContent}>
                    <HeaderRowComponent dates={dates} />
                    <div className={styles.virtualContainer} style={{ height: `${totalHeight}px` }}>
                        {renderedRows}
                    </div>
                    <div className={styles.totalRowSticky}>
                        <TotalRowComponent dates={dates} onAllocationProjectClick={onAllocationProjectClick} />
                    </div>
                </div>
            </div>
            <SubmitSuccessOverlay />
        </div>
    );
});
